import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { Navigate, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import axios from 'axios';
import { Check, Crown, Sparkle, Lightning } from '@phosphor-icons/react';
import { formatINR } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIER_META = {
  free: {
    name: 'Free',
    tagline: 'Get started with basic tools',
    accent: '#94A3B8',
    icon: Sparkle,
    features: [
      'Basic stock screener (Nifty 50)',
      '1 watchlist',
      '3 AI trade ideas per day',
      'Market news feed',
      'Nifty 50 / Sensex indices',
      'FII/DII activity',
    ],
    locked: [
      'Next Day Movers',
      'Technical indicators',
      'Portfolio analytics',
      'Options chain',
      'AI Pattern Detection',
    ],
  },
  pro: {
    name: 'Pro',
    tagline: 'For serious retail traders',
    accent: '#A855F7',
    icon: Lightning,
    features: [
      'Everything in Free',
      'Full stock screener with all filters',
      'Unlimited watchlists',
      '20 AI trade ideas per day',
      'Next Day Movers (AI-picked)',
      'Technical indicators (RSI, MACD, Bollinger)',
      'Portfolio analytics with risk metrics',
      'Live P&L tracking',
    ],
    locked: [
      'Options chain with Greeks',
      'AI Pattern Detection',
      'Live alerts',
      'Priority support',
    ],
  },
  elite: {
    name: 'Elite',
    tagline: 'For professional & F&O traders',
    accent: '#FBBF24',
    icon: Crown,
    features: [
      'Everything in Pro',
      'Unlimited AI trade ideas',
      'Options chain (Nifty, Bank Nifty) with Greeks',
      'AI Pattern Detection (GPT-5.2)',
      'Live price alerts',
      'Priority support (24/7)',
    ],
    locked: [],
  },
};

const currencyFormatIN = (v) => v.toLocaleString('en-IN');

export default function Pricing() {
  const { user, loading: authLoading } = useAuth();
  const { tier: currentTier, refresh: refreshSub } = useSubscription();
  const [billing, setBilling] = useState('monthly');
  const [plans, setPlans] = useState({});
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  useEffect(() => {
    axios.get(`${API}/plans`, { withCredentials: true })
      .then(({ data }) => {
        const map = {};
        for (const p of data.plans) map[p.lookup_key] = p;
        setPlans(map);
      })
      .catch(() => setPlans({}));
  }, []);

  const handleCheckout = async (tierKey) => {
    if (!user) {
      window.location.href = '/login';
      return;
    }
    const lookupKey = `${tierKey}_${billing === 'monthly' ? 'monthly' : 'yearly'}`;
    setCheckoutLoading(tierKey);
    try {
      const { data } = await axios.post(
        `${API}/subscriptions/checkout`,
        { lookup_key: lookupKey, origin_url: window.location.origin },
        { withCredentials: true }
      );
      window.location.href = data.checkout_url;
    } catch (e) {
      setCheckoutLoading(null);
      alert('Unable to start checkout. Please try again.');
    }
  };

  if (authLoading) return <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0B0E' }}><div className="text-white">Loading...</div></div>;

  const proPlan = plans[`pro_${billing}`];
  const elitePlan = plans[`elite_${billing}`];

  const yearlySavings = (monthlyPrice, yearlyPrice) => {
    const fullYear = monthlyPrice * 12;
    return Math.round(((fullYear - yearlyPrice) / fullYear) * 100);
  };

  const proMonthlyPrice = plans.pro_monthly?.amount || 499;
  const proYearlyPrice = plans.pro_yearly?.amount || 4790;
  const eliteMonthlyPrice = plans.elite_monthly?.amount || 1999;
  const eliteYearlyPrice = plans.elite_yearly?.amount || 19190;

  const Content = () => (
    <div className="w-full max-w-6xl mx-auto px-6 py-12" data-testid="pricing-page">
      {/* Header */}
      <div className="text-center mb-10">
        <h1 className="text-5xl font-heading font-black tracking-tighter text-white mb-3">
          Choose your edge
        </h1>
        <p className="text-lg text-[#94A3B8] max-w-2xl mx-auto">
          Simple pricing. Cancel anytime. 7-day free trial on Pro & Elite.
        </p>

        {/* Billing toggle */}
        <div className="inline-flex gap-1 border border-white/10 p-1 mt-6" data-testid="billing-toggle">
          <button
            onClick={() => setBilling('monthly')}
            className={`px-4 py-2 text-sm font-mono transition-colors ${billing === 'monthly' ? 'bg-white text-black' : 'text-[#94A3B8] hover:text-white'}`}
            data-testid="billing-monthly"
          >
            Monthly
          </button>
          <button
            onClick={() => setBilling('yearly')}
            className={`px-4 py-2 text-sm font-mono transition-colors ${billing === 'yearly' ? 'bg-white text-black' : 'text-[#94A3B8] hover:text-white'}`}
            data-testid="billing-yearly"
          >
            Yearly <span className="text-[#00FF66]">−20%</span>
          </button>
        </div>
      </div>

      {/* Plans */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Free */}
        <PlanCard
          meta={TIER_META.free}
          price={0}
          billing={billing}
          currentTier={currentTier}
          tierKey="free"
          onCheckout={handleCheckout}
          loading={checkoutLoading === 'free'}
        />

        {/* Pro */}
        <PlanCard
          meta={TIER_META.pro}
          price={billing === 'monthly' ? proMonthlyPrice : proYearlyPrice}
          savings={billing === 'yearly' ? yearlySavings(proMonthlyPrice, proYearlyPrice) : null}
          billing={billing}
          currentTier={currentTier}
          tierKey="pro"
          featured
          onCheckout={handleCheckout}
          loading={checkoutLoading === 'pro'}
        />

        {/* Elite */}
        <PlanCard
          meta={TIER_META.elite}
          price={billing === 'monthly' ? eliteMonthlyPrice : eliteYearlyPrice}
          savings={billing === 'yearly' ? yearlySavings(eliteMonthlyPrice, eliteYearlyPrice) : null}
          billing={billing}
          currentTier={currentTier}
          tierKey="elite"
          onCheckout={handleCheckout}
          loading={checkoutLoading === 'elite'}
        />
      </div>

      {/* FAQ / Trust */}
      <div className="mt-16 text-center text-xs text-[#64748B] font-mono">
        Secure payments by Stripe • Cancel anytime • Data protected as per SEBI/RBI guidelines
      </div>
    </div>
  );

  if (user) return <Layout><Content /></Layout>;
  return <div style={{ background: '#0A0B0E' }} className="min-h-screen"><Content /></div>;
}

function PlanCard({ meta, price, savings, billing, currentTier, tierKey, featured, onCheckout, loading }) {
  const Icon = meta.icon;
  const isCurrent = currentTier === tierKey;
  const isFree = tierKey === 'free';

  return (
    <div
      className={`bg-[#12141A] p-6 flex flex-col relative ${featured ? 'border-2' : 'border'}`}
      style={{ borderColor: featured ? meta.accent : 'rgba(255,255,255,0.1)' }}
      data-testid={`plan-${tierKey}`}
    >
      {featured && (
        <div
          className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-[10px] font-mono uppercase tracking-widest text-black"
          style={{ background: meta.accent }}
        >
          Most Popular
        </div>
      )}

      <div className="flex items-center gap-2 mb-2">
        <Icon size={28} weight="duotone" style={{ color: meta.accent }} />
        <h3 className="text-2xl font-heading font-bold text-white">{meta.name}</h3>
      </div>
      <p className="text-sm text-[#94A3B8] mb-6">{meta.tagline}</p>

      {/* Price */}
      <div className="mb-6">
        <div className="flex items-baseline gap-1">
          <span className="text-4xl font-mono font-black text-white">
            {isFree ? 'Free' : `₹${currencyFormatIN(price)}`}
          </span>
          {!isFree && (
            <span className="text-sm text-[#64748B]">/{billing === 'monthly' ? 'mo' : 'yr'}</span>
          )}
        </div>
        {savings && (
          <div className="text-xs text-[#00FF66] font-mono mt-1">Save {savings}% vs monthly</div>
        )}
        {!isFree && billing === 'monthly' && (
          <div className="text-xs text-[#94A3B8] font-mono mt-1">7-day free trial</div>
        )}
      </div>

      {/* CTA */}
      {isCurrent ? (
        <div className="w-full py-3 text-center border border-white/10 text-[#94A3B8] text-sm font-medium mb-6" data-testid={`current-${tierKey}`}>
          Current Plan
        </div>
      ) : isFree ? (
        <Link
          to="/dashboard"
          className="w-full py-3 text-center border border-white/10 text-white hover:bg-white/[0.02] text-sm font-medium mb-6 block"
          data-testid={`cta-${tierKey}`}
        >
          Get Started Free
        </Link>
      ) : (
        <button
          onClick={() => onCheckout(tierKey)}
          disabled={loading}
          className="w-full py-3 text-black text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 mb-6"
          style={{ background: meta.accent }}
          data-testid={`cta-${tierKey}`}
        >
          {loading ? 'Opening checkout...' : `Upgrade to ${meta.name}`}
        </button>
      )}

      {/* Features list */}
      <div className="space-y-2 flex-1">
        {meta.features.map((f) => (
          <div key={f} className="flex items-start gap-2 text-sm">
            <Check size={16} weight="bold" className="mt-0.5 flex-shrink-0" style={{ color: meta.accent }} />
            <span className="text-white">{f}</span>
          </div>
        ))}
        {meta.locked && meta.locked.length > 0 && (
          <div className="pt-3 mt-3 border-t border-white/5 space-y-2">
            {meta.locked.map((f) => (
              <div key={f} className="flex items-start gap-2 text-sm opacity-40">
                <span className="mt-0.5 flex-shrink-0 w-4 h-4 flex items-center justify-center">×</span>
                <span className="text-[#94A3B8]">{f}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
